import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { TokenService } from './token.service';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

/**
 * Functional HTTP interceptor that:
 * 1. Attaches `Authorization: Bearer <accessToken>` header from TokenService
 * 2. Handles 401 responses by triggering logout via AuthService
 *
 * Uses Angular 17+ `HttpInterceptorFn` pattern (no class-based interceptor).
 *
 * Requirements: 9.5
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const authService = inject(AuthService);

  // Skip auth header for external URLs (e.g., S3 presigned URLs)
  if (!req.url.startsWith('/') && !req.url.includes(environment.domain)) {
    return next(req).pipe(
      tap({
        error: (error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            authService.signOut();
          }
        },
      }),
    );
  }

  const accessToken = tokenService.getAccessToken();

  // Clone the request with the Authorization header if a token is available
  const authReq = accessToken
    ? req.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
    : req;

  return next(authReq).pipe(
    tap({
      error: (error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          // Token is invalid or expired — trigger logout
          authService.signOut();
        }
      },
    }),
  );
};
